import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Award, Heart, ArrowRight, ShieldCheck, Check, Star } from "lucide-react";
import { agent, images, vaCredentials } from "../mock";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Testimonials } from "../components/Sections";
import { ApplyWithSharon } from "../components/Lender";

const values = [
  { icon: Heart, title: "People Over Paperwork", body: "You're not a credit score to me. You're a family with a dream, and I treat you that way." },
  { icon: Award, title: "Top-Ranking Results", body: "A consistent top sales & listing agent in the Corridor — because I negotiate like it's my own home." },
  { icon: Phone, title: "Always Reachable", body: "Call, text, or email — you'll never feel like just another transaction on my calendar." },
];

const AboutMe = () => {
  return (
    <div>
      <PageHero
        eyebrow="About Me"
        title="Hi, I'm Kristy Russo"
        subtitle="Top-ranking RE/MAX Corridor agent — and the realtor who fights for buyers everyone else gave up on."
        image={images.interiors[1]}
      />

      {/* VA SPECIALIST CREDENTIALS */}
      <section className="bg-[#141210]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-center">
            <div className="lg:col-span-3">
              <span className="inline-flex items-center gap-2 bg-[#d4af37] text-white text-[10px] tracking-luxury uppercase px-4 py-2 rounded-full mb-6">
                <Star className="w-3 h-3 fill-white" /> {vaCredentials.ribbon}
              </span>
              <h2 className="font-display text-4xl md:text-6xl text-[#f4ebd9] leading-tight">
                {vaCredentials.name}
              </h2>
              <p className="text-gold text-lg md:text-xl mt-3 mb-7 font-medium">
                {vaCredentials.title}
              </p>
              {vaCredentials.paragraphs.map((p) => (
                <p key={p.slice(0, 24)} className="text-[#cabb9f] leading-relaxed mb-5 max-w-2xl">
                  {p}
                </p>
              ))}
              <div className="mt-8">
                <ApplyWithSharon label="Start Your VA Pre-Approval" />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#0e0c0a] rounded-3xl p-8 md:p-9 border border-[#2a241d]">
                <div className="flex items-center gap-3 mb-6">
                  <ShieldCheck className="w-6 h-6 text-gold" />
                  <h3 className="font-display text-2xl text-[#f4ebd9]">Proven Track Record</h3>
                </div>
                <ul className="space-y-4">
                  {vaCredentials.achievements.map((a) => (
                    <li key={a.slice(0, 24)} className="flex items-start gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#d4af37] flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#141210]" />
                      </span>
                      <span className="text-sm text-[#e9dcc4] leading-relaxed">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-14 border-t border-[#2a241d] pt-10 text-center">
            <p className="font-display text-2xl md:text-3xl text-[#f4ebd9] italic">
              {vaCredentials.closing}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">
          <div className="lg:col-span-2">
            <div className="img-zoom rounded-3xl overflow-hidden shadow-[0_30px_70px_-40px_rgba(84,64,34,0.5)] sticky top-28">
              <img
                src={agent.photo}
                alt="Kristy Russo, Realtor"
                className="w-full h-[560px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <span className="text-xs tracking-luxury uppercase text-gold">My Story</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4 mb-6 leading-tight">
              Real estate with heart, backed by results.
            </h2>
            <div className="space-y-5 text-[#5c5348] leading-relaxed">
              <p>
                For years I've had the honor of guiding families across the Texas Hill Country into
                homes they were told they'd never own. As a top-ranking sales and listing agent with{" "}
                <strong>RE/MAX Corridor</strong>, I bring big-league marketing and negotiation to
                every client — but it's the human side that drives me.
              </p>
              <p>
                My specialty is <strong>VA loans</strong> and <strong>credit-challenged buyers</strong>.
                Thanks to a genuinely flexible lender partner, I can frequently open doors that stayed
                shut elsewhere — including VA loans with <em>no minimum credit score*</em>.
              </p>
              <p>
                Whether you're a veteran using your hard-earned benefits, a first-time buyer rebuilding
                after a setback, or a seller ready for a premium, casual-luxury listing experience —
                I'd be honored to walk beside you.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-[#efe6d6] hover-lift">
                  <div className="w-11 h-11 rounded-full bg-[#f7edcf] flex items-center justify-center mb-4">
                    <v.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-display text-xl text-[#141210] mb-2">{v.title}</h3>
                  <p className="text-sm text-[#5c5348] leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href={agent.phoneHref}>
                <Button className="bg-[#d4af37] hover:bg-[#c19a2e] text-white rounded-full px-7 h-12 tracking-luxury uppercase text-xs w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" /> {agent.phone}
                </Button>
              </a>
              <a href={agent.emailHref}>
                <Button variant="outline" className="border-[#d8c9ac] text-[#141210] hover:bg-[#f7edcf] rounded-full px-7 h-12 tracking-luxury uppercase text-xs w-full sm:w-auto">
                  <Mail className="w-4 h-4 mr-2" /> Email Me
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f4f1ea]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs tracking-luxury uppercase text-gold">Kind Words</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#141210] mt-4">
              What my clients say
            </h2>
          </div>
          <Testimonials />
          <div className="text-center mt-14">
            <Link to="/apply-to-buy">
              <Button className="bg-[#141210] hover:bg-[#2a241d] text-white rounded-full px-9 h-13 py-6 tracking-luxury uppercase text-xs">
                Let's Work Together <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutMe;
